import { Platform } from 'react-native';

/**
 * Version Manager & Hard Refresh on New Deployed Build
 *
 * Automatically detects when a new build or deployment is released to production/staging.
 * When a new version/build timestamp is detected:
 *   1. Clears CacheStorage (window.caches)
 *   2. Unregisters obsolete caching service workers
 *   3. Performs a cache-busted hard refresh so the user immediately runs the latest code
 *
 * Triggers:
 *   - On initial application load
 *   - On window focus / tab visibility change (e.g. user returns to an existing tab after deployment)
 *   - Periodic background check (every 5 minutes)
 */

export const isWeb =
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof window.location !== 'undefined';

const isDev =
  (typeof __DEV__ !== 'undefined' && __DEV__) ||
  (isWeb && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

const STORAGE_KEY_BUILD_TIME = 'openbrix_installed_build_time';
const SESSION_KEY_RELOADED_FOR = 'openbrix_reloaded_build_time';
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const MIN_CHECK_INTERVAL_MS = 15 * 1000; // Throttle: at least 15s between checks

// Current build time injected by webpack DefinePlugin during build
const EMBEDDED_BUILD_TIME =
  typeof process !== 'undefined' && process.env && process.env.APP_BUILD_TIME
    ? String(process.env.APP_BUILD_TIME)
    : null;

let isChecking = false;
let lastCheckTime = 0;
let checkTimer = null;

// Clean up any temporary _reload query parameter from URL so the address bar stays clean
if (isWeb) {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_reload')) {
      url.searchParams.delete('_reload');
      const cleanUrl = url.pathname + url.search + url.hash;
      window.history.replaceState({}, document.title, cleanUrl || '/');
    }
  } catch (e) {
    // Ignore URL manipulation errors
  }
}

/**
 * Clear all browser CacheStorage instances safely
 */
export const clearBrowserCaches = async () => {
  if (isWeb && 'caches' in window) {
    try {
      const cacheNames = await window.caches.keys();
      await Promise.all(cacheNames.map((name) => window.caches.delete(name)));
      console.log('[VersionManager] Browser CacheStorage cleared.');
    } catch (e) {
      console.warn('[VersionManager] Failed to clear CacheStorage:', e);
    }
  }
};

/**
 * Remove obsolete service workers that might cache HTML/JS assets
 */
export const cleanObsoleteServiceWorkers = async () => {
  if (isWeb && 'serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        if (
          reg.active &&
          reg.active.scriptURL &&
          (reg.active.scriptURL.indexOf('expo-service-worker') !== -1 ||
            reg.active.scriptURL.indexOf('service-worker.js') !== -1)
        ) {
          await reg.unregister();
          console.log('[VersionManager] Unregistered obsolete service worker:', reg.active.scriptURL);
        } else if (reg && typeof reg.update === 'function') {
          reg.update().catch(() => {});
        }
      }
    } catch (e) {
      console.warn('[VersionManager] Error cleaning service workers:', e);
    }
  }
};

/**
 * Perform a true hard reload with cache-busting
 * @param {string} newBuildTime
 */
export const performHardRefresh = async (newBuildTime) => {
  if (!isWeb) return;

  // Never auto-reload developers on localhost during active development
  if (isDev && !window.__FORCE_VERSION_CHECK__) {
    console.log('[VersionManager] [DEV MODE] New build detected, skipping auto-reload on localhost.');
    return;
  }

  const targetBuild = String(newBuildTime || Date.now());

  // Prevent reload loops in the current browser tab session
  try {
    const alreadyReloadedFor = sessionStorage.getItem(SESSION_KEY_RELOADED_FOR);
    if (alreadyReloadedFor && alreadyReloadedFor === targetBuild) {
      console.log('[VersionManager] Already reloaded for build', targetBuild, '- skipping reload loop.');
      return;
    }
    sessionStorage.setItem(SESSION_KEY_RELOADED_FOR, targetBuild);
    localStorage.setItem(STORAGE_KEY_BUILD_TIME, targetBuild);
  } catch (e) {
    // sessionStorage/localStorage might fail in restricted environments
  }

  console.log('[VersionManager] New build detected (' + targetBuild + '). Cleaning cache and hard refreshing...');

  try {
    await clearBrowserCaches();
    await cleanObsoleteServiceWorkers();
  } catch (e) {
    console.warn('[VersionManager] Error during pre-refresh cleanup:', e);
  }

  // Force cache-busting reload by updating the URL with a timestamp query param
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_reload', String(Date.now()));
    window.location.replace(url.toString());
  } catch (e) {
    window.location.reload();
  }
};

/**
 * Fetch remote version.json and check if an update is available
 * @returns {Promise<boolean>} Whether an update was detected
 */
export const checkForAppUpdate = async () => {
  if (!isWeb) return false;

  const now = Date.now();
  if (isChecking || now - lastCheckTime < MIN_CHECK_INTERVAL_MS) {
    return false;
  }

  isChecking = true;
  lastCheckTime = now;

  try {
    // Fetch /version.json with strict cache-busting headers
    const response = await fetch(`/version.json?_t=${now}`, {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    });

    if (!response.ok) {
      isChecking = false;
      return false;
    }

    const data = await response.json();
    const remoteBuildTime = data?.buildTime ? String(data.buildTime) : null;

    if (!remoteBuildTime) {
      isChecking = false;
      return false;
    }

    // Determine current local build time
    let localBuildTime = null;
    try {
      localBuildTime = localStorage.getItem(STORAGE_KEY_BUILD_TIME);
    } catch (e) {}

    // First visit: record the current build time
    if (!localBuildTime) {
      try {
        localStorage.setItem(STORAGE_KEY_BUILD_TIME, remoteBuildTime);
      } catch (e) {}
      // If we also have an embedded build time from Webpack and it differs from server:
      if (EMBEDDED_BUILD_TIME && EMBEDDED_BUILD_TIME !== remoteBuildTime) {
        await performHardRefresh(remoteBuildTime);
        return true;
      }
      isChecking = false;
      return false;
    }

    // Check if remote build differs from local installed build
    const isLocalMismatch = localBuildTime !== remoteBuildTime;
    const isEmbeddedMismatch = EMBEDDED_BUILD_TIME && EMBEDDED_BUILD_TIME !== remoteBuildTime;

    if (isLocalMismatch || isEmbeddedMismatch) {
      console.log(
        `[VersionManager] Remote build (${remoteBuildTime}) differs from local (${localBuildTime}) or embedded (${EMBEDDED_BUILD_TIME}). Refreshing...`
      );
      await performHardRefresh(remoteBuildTime);
      return true;
    }

    isChecking = false;
    return false;
  } catch (error) {
    // Network failure or offline — silently ignore
    isChecking = false;
    return false;
  }
};

/**
 * Initialize listeners for automatic version checking and hard refresh
 */
export const initVersionManager = () => {
  if (!isWeb) {
    return () => {};
  }

  // 1. Initial check shortly after load so initial rendering is smooth
  const initialTimer = setTimeout(() => {
    checkForAppUpdate();
  }, 2500);

  // 2. Check whenever user returns to the tab/window
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkForAppUpdate();
    }
  };

  const handleFocus = () => {
    checkForAppUpdate();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handleFocus);

  // 3. Periodic background check every 5 minutes
  if (!checkTimer) {
    checkTimer = setInterval(() => {
      checkForAppUpdate();
    }, CHECK_INTERVAL_MS);
  }

  // Return teardown function
  return () => {
    clearTimeout(initialTimer);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', handleFocus);
    if (checkTimer) {
      clearInterval(checkTimer);
      checkTimer = null;
    }
  };
};

export default {
  initVersionManager,
  checkForAppUpdate,
  performHardRefresh,
  clearBrowserCaches,
  cleanObsoleteServiceWorkers,
};
